import React, { useState, useEffect } from "react";
import Select from "react-select";
import { AdminServices } from "../../services/admin/AdminServices";

// Type Definitions
type AudienceFormData = {
  openingHours: number;
  closingHours: number;
  category: string;
  subCategory: string;
  socialIndicator: string;
  persona: string[];
  incomeCategory: string[];
  gender: string;
  averageMonthlyFootfall: number;
  ageGroup: string[];
  notAllowedCategory: string[];
  notAllowedSubCategory: string[];
};

type CategoryOption = {
  value: string;
  label: string;
};

type SubCategoryOption = {
  value: string;
  label: string;
  category: string; // The category code that links it to the category
};

type AudienceDataFormProps = {
  audienceFormData: AudienceFormData;
  setAudienceFormData: React.Dispatch<React.SetStateAction<AudienceFormData>>;
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
};

const AudienceDataForm: React.FC<AudienceDataFormProps> = ({
  audienceFormData,
  setAudienceFormData,
  counter,
  setCounter,
}) => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<SubCategoryOption[]>([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState<SubCategoryOption[]>([]);
  const [notAllowedCategories, setNotAllowedCategories] = useState<CategoryOption[]>([]);

  const [notAllowedSubCategory, setNotAllowedSubCategory] = useState<SubCategoryOption[]>([]);


  // Fetch Categories and Subcategories - only on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesData = await AdminServices.fetchCategories();

        if (categoriesData && Array.isArray(categoriesData.response)) {
          const formattedCategories = categoriesData.response.map((cat: any) => ({
            value: cat.code,
            label: cat.name,
          }));
          setCategoryOptions(formattedCategories);
        } else {
          console.error("Error occurred while fetching categories:", categoriesData );
        }

        // Fetch subcategories
        const subCategoriesData = await AdminServices.fetchSubcategories();

        if (subCategoriesData && Array.isArray(subCategoriesData.response)) {
          const formattedSubCategories = subCategoriesData.response.map((sub: any) => ({
            value: sub.code ?? sub.value,
            label: sub.name,
            category: sub.categoryCode,
          }));
          setSubCategoryOptions(formattedSubCategories);
        } else {
          console.error("Error occurred while fetching subcategories:", subCategoriesData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []); // ✅ runs only once



  useEffect(() => {
    setNotAllowedCategories(categoryOptions);
  }, [categoryOptions]);
  useEffect(() => {
    

    setNotAllowedSubCategory(subCategoryOptions);
    
  }, [subCategoryOptions]);


  // Filter Subcategories based on selected Category
  useEffect(() => {
    const updatedSubCategories = subCategoryOptions.filter(
      (sub: SubCategoryOption) => sub.category === audienceFormData.category
    );
    setFilteredSubCategories(updatedSubCategories);
  }, [audienceFormData.category, subCategoryOptions]);



  // Handle going back in the form
  const handleBack = () => {
    setCounter(counter - 1);
  };

  // Handle changes in the React Select fields
  const handleReactSelectChange = (
    value: any,
    field: keyof AudienceFormData,
    isMulti: boolean = false
  ) => {
    const selected = isMulti
      ? (value as { value: string; label: string }[]).map((opt) => opt.value)
      : (value as { value: string; label: string })?.value ?? "";

    setAudienceFormData((prev) => ({
      ...prev,
      [field]: selected,
    }));
  };

  // Options for the various form fields
  const hoursOptions = Array.from({ length: 24 }, (_, i) => ({
    label: `${i.toString().padStart(2, "0")}:00`,
    value: i * 60,
  }));


  //updated socialIndicatorOptions
  const socialIndicatorOptions = ["BASIC", "STANDARD", "PREMIUM", "ULTRA_PREMIUM", "LUXURY"];



  //updated persona
  const personaOptions = [
    { label: "Infants & Toddlers", value: "INFANTS_AND_TODDLERS" },
    { label: "Preschoolers", value: "PRESCHOOLERS" },
    { label: "Teens", value: "TEENS" },
    { label: "Adolescents", value: "ADOLESCENTS" },
    { label: "Young Adults", value: "YOUNG_ADULTS" },
    { label: "New Families", value: "NEW_FAMILIES" },
    { label: "Established Families", value: "ESTABLISHED_FAMILIES" },
    { label: "Mature Adults", value: "MATURE_ADULTS" },
    { label: "Senior Citizens", value: "SENIOR_CITIZENS" }
  ];


  //incomecategoriesupdated
  const incomeCategoryOptions = [
    { label: "0-3 Lakhs", value: "ZERO_TO_THREE_LAKHS" },
    { label: "3-7.5 Lakhs", value: "THREE_TO_SEVEN_POINT_FIVE_LAKHS" },
    { label: "7.5-15 Lakhs", value: "SEVEN_POINT_FIVE_TO_FIFTEEN_LAKHS" },
    { label: "15-25 Lakhs", value: "FIFTEEN_TO_TWENTY_FIVE_LAKHS" },
    { label: "25-50 Lakhs", value: "TWENTY_FIVE_TO_FIFTY_LAKHS" },
    { label: "50+ Lakhs", value: "FIFTY_PLUS_LAKHS" },
  ];
  // agegroupoptions updated
  const ageGroupOptions = [
    { label: "0-4", value: "ZERO_TO_FOUR" },
    { label: "4-7", value: "FOUR_TO_SEVEN" },
    { label: "7-12", value: "SEVEN_TO_TWELVE" },
    { label: "12-18", value: "TWELVE_TO_EIGHTEEN" },
    { label: "18-29", value: "EIGHTEEN_TO_TWENTY_NINE" },
    { label: "29-40", value: "TWENTY_NINE_TO_FORTY" },
    { label: "40-50", value: "FORTY_TO_FIFTY" },
    { label: "50-60", value: "FIFTY_TO_SIXTY" },
    { label: "60+", value: "SIXTY_PLUS"},
  ];

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      width: '100%',
      padding: '0.6rem',
      border: '1px solid #d4d4d8', // Tailwind's border-neutral-300
      borderRadius: '0.75rem',  
    }),
    option: (provided: any) => ({
      ...provided,
      color: '#4b5563', // Tailwind's neutral-400
      fontSize: '0.875rem',
      
    }),
  };

  return (
    <form className="relative grid grid-cols-1 gap-6 pb-20 mx-auto md:grid-cols-2">
      {/* Opening Hours */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Opening Hours <span className="text-red-500">*</span>
        </label>
        <Select
          value={hoursOptions.find(opt => opt.value === audienceFormData.openingHours)}
          onChange={(val: any) => handleReactSelectChange(val, "openingHours")}
          options={hoursOptions}
          styles={customStyles}
        />
      </div>

      {/* Closing Hours */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Closing Hours <span className="text-red-500">*</span></label>
        <Select
          value={hoursOptions.find(opt => opt.value === audienceFormData.closingHours)}
          onChange={(val: any) => handleReactSelectChange(val, "closingHours")}
          options={hoursOptions}
          styles={customStyles}
        />
      </div>

      {/* Category */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Category <span className="text-red-500">*</span></label>
        <Select
          value={categoryOptions.find(opt => opt.value === audienceFormData.category)}
          onChange={(val: any) => handleReactSelectChange(val, "category")}
          options={categoryOptions}
          styles={customStyles}
        />
      </div>

      {/* Subcategory */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Subcategory <span className="text-red-500">*</span></label>
        <Select
          value={filteredSubCategories.find(opt => opt.value === audienceFormData.subCategory)}
          onChange={(val: any) => handleReactSelectChange(val, "subCategory")}
          options={filteredSubCategories}
          styles={customStyles}
        />
      </div>

      {/* Social Indicator */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Social Indicator <span className="text-red-500">*</span></label>
        <Select
          value={{ label: audienceFormData.socialIndicator, value: audienceFormData.socialIndicator }}
          onChange={(val: any) => handleReactSelectChange(val, "socialIndicator")}
          options={socialIndicatorOptions.map(item => ({ label: item, value: item }))}
          styles={customStyles}
        />
      </div>

      {/* Persona */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Persona <span className="text-red-500">*</span></label>
        <Select
          isMulti
          options={personaOptions}
          value={personaOptions.filter(item => audienceFormData.persona.includes(item.value))}
          onChange={(selected: any) => handleReactSelectChange(selected, "persona", true)}
          styles={customStyles}
          closeMenuOnSelect={false}
        />
      </div>

      {/* Income Category */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Income Category <span className="text-red-500">*</span></label>
        <Select
          isMulti
          options={incomeCategoryOptions}
          value={incomeCategoryOptions.filter(opt =>
            audienceFormData.incomeCategory.includes(opt.value)
          )}
          onChange={(val: any) => handleReactSelectChange(val, "incomeCategory", true)}
          styles={customStyles}
          closeMenuOnSelect={false}
        />
      </div>

      {/* Gender */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Gender <span className="text-red-500">*</span></label>
        <Select
          value={{ label: audienceFormData.gender, value: audienceFormData.gender }}
          onChange={(val: any) => handleReactSelectChange(val, "gender")}
          options={[
            { label: "Male", value: "MALE" },
            { label: "Female", value: "FEMALE" },
            { label: "Unisex", value: "UNISEX" }
          ]}
          styles={customStyles}
        />
      </div>

      {/* Average Monthly Footfall */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Average Monthly Footfall <span className="text-red-500">*</span></label>
        <input
          type="number"
          name="averageMonthlyFootfall"
          value={audienceFormData.averageMonthlyFootfall ?? ""}
          onChange={(e) =>
            setAudienceFormData(prev => ({
              ...prev,
              averageMonthlyFootfall: parseInt(e.target.value)
            }))
          }
          className="w-full p-4 border rounded-xl border-neutral-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>

      {/* Age Group */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Age Group <span className="text-red-500">*</span></label>
        <Select
          isMulti
          value={ageGroupOptions.filter(opt =>
            audienceFormData.ageGroup.includes(opt.value)
          )}
          onChange={(val: any) => handleReactSelectChange(val, "ageGroup", true)}
          options={ageGroupOptions}
          styles={customStyles}
          closeMenuOnSelect={false}
        />
      </div>

      {/* here render the not allowed category statre same as category is rendered above  */}
      {/* Not Allowed Category */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Not Allowed Category</label>
        <Select
          isMulti
          value={notAllowedCategories.filter(opt =>
            audienceFormData.notAllowedCategory.includes(opt.value)
          )}
          onChange={(val: any) => handleReactSelectChange(val, "notAllowedCategory", true)}
          options={notAllowedCategories}
          styles={customStyles}
          closeMenuOnSelect={false}
        />
      </div>

      {/* Not Allowed Subcategory */}
      <div className="flex flex-col">
        <label className="mb-1 text-lg">Not Allowed Subcategory</label>
        <Select
          isMulti
          value={notAllowedSubCategory.filter(opt =>
            audienceFormData.notAllowedSubCategory.includes(opt.value)
          )}
          onChange={(val: any) => handleReactSelectChange(val, "notAllowedSubCategory", true)}
          options={notAllowedSubCategory}
          styles={customStyles}
          closeMenuOnSelect={false}
        />
      </div>



      {/* Back Button */}
      <div className="flex w-[200%] justify-end">
        <button
          type="button"
          onClick={handleBack}
          className="px-6 py-2 font-semibold transition bg-white border rounded text-violet-500 border-violet-500 hover:bg-violet-50"
        >
          Back
        </button>
      </div>
    </form>
  );
};

export default AudienceDataForm;
